<template>
    <div class="pa-5">
        <v-card v-if="data">
            <v-card-title>
                <h1 class="title">{{ 'Locations' | term }}</h1>
                <split-button class="ml-3" v-if="selectedMineArea" color="secondary" @click="onClickAddLocation">
                    <template #items>
                        <v-list-item v-for='quickAccessType in permittedLocationTypesQuickAccess' :key='quickAccessType.name' @click='quickAccessType.action'>
                            <v-list-item-title>{{ quickAccessType.name }}</v-list-item-title>
                        </v-list-item>
                    </template>
                    <v-icon left>mdi-plus</v-icon>Add
                </split-button>
                <v-btn small color="primary" class="ml-3" v-if="allowEquipmentLocations" @click="createEquipmentLocation">
                    <v-icon left>mdi-truck-outline</v-icon>Track {{ 'Equipment' | term }}
                </v-btn>
                <v-menu
                    v-model="importDialog"
                    max-width="310px"
                    :close-on-content-click="false"
                    v-if="!!selectedMineArea"
                >
                    <template v-slot:activator="{ on }">
                        <v-alert
                            class="cannot-import"
                            dense
                            outlined
                            border="left"
                            type="warning"
                            v-if="filteredReducedLevels.length === 0"
                        >
                            Cannot import: No reduced levels available for this mine area.
                        </v-alert>
                        <v-btn
                            v-on="on"
                            small
                            class="ml-1"
                            color="secondary"
                            :disabled="filteredReducedLevels.length === 0"
                        >
                            <v-icon left>mdi-upload</v-icon>Import
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-text>
                            <input ref="inputUpload" type="file" accept="text/csv" @change="getFile($event)" />
                        </v-card-text>
                        <v-card-actions>
                            <v-btn color="secondary" :disabled="file === null" @click="importData">Upload</v-btn>
                            <v-btn text @click="importDialog = false">cancel</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <v-btn
                    small
                    class="ml-1"
                    color="gray"
                    v-if="!!selectedMineArea && filtered.length"
                    @click="exportData()"
                >
                    <v-icon left>mdi-download</v-icon>Export
                </v-btn>
                <v-btn small color="primary" class="ml-3" v-if="selectedMineArea && dirty" @click="saveList()">
                    <v-icon left>mdi-check</v-icon>Save
                </v-btn>
                <v-btn small color="grey" class="ml-3" v-if="selectedMineArea && dirty" @click="loadData()">
                    <v-icon left>mdi-close</v-icon>Cancel
                </v-btn>
                <v-spacer></v-spacer>
                <v-select
                    v-if="departmentDropdownOptions"
                    :items="departmentDropdownOptions"
                    item-value="id"
                    item-text="name"
                    v-model="selectedDepartment"
                    label="Department"
                    single-line
                    hide-details
                    class="admin-table-filter"
                    @change="selectMineArea()"
                    :menu-props="{ dark: true }"
                >
                    <template v-slot:item="data">
                        <template v-if="data.item.isGroup">
                            <v-list-item-content
                                class="caption"
                                @click="$event.stopPropagation()"
                                v-text="data.item.name"
                            ></v-list-item-content>
                        </template>
                        <template v-else>
                            <v-list-item-content class="pl-5" v-text="data.item.name"></v-list-item-content>
                        </template>
                    </template>
                </v-select>
            </v-card-title>
            <v-card-title v-if="!!selectedDepartment ">
                <h2 class='subtitle-1'>{{ 'Location' | termPlural }}
                    <v-tooltip right>
                    <template v-slot:activator='{ on }'>
                        <v-icon small right v-on='on'>mdi-information-outline</v-icon>
                    </template>
                    <span>Allows planning tasks against {{ 'equipment' | termPlural }} instead of a {{ 'location' | term }}.</span>
                </v-tooltip></h2>
            </v-card-title>
            <v-window v-model="dataTableDisplayWindow">
                <v-window-item :value="0">
                    <standard-wrapping-reorderable
                        v-if="!!selectedDepartment"
                        :items="filteredActiveLocationsForDisplay"
                        :item-id="'id'"
                        :item-order="'displayOrder'"
                        @reordered="onItemsReordered"
                        item-class="locations-item"
                        handle-class="locations-handle"
                    >
                        <v-data-table
                            disable-pagination
                            hide-default-footer
                            :headers="locationHeaders"
                            :items="filteredActiveLocationsForDisplay"

                        >
                            <template v-slot:item="{ item }">
                                <tr :class="{ dirty: item.dirty, 'locations-item': true }">
                                    <td>
                                        <div class="no-wrap">
                                            <v-icon class="locations-handle">mdi-drag</v-icon>
                                        </div>
                                    </td>
                                    <td v-if="allowEquipmentLocations">
                                        <v-tooltip v-if="item.equipmentId" right>
                                            <template v-slot:activator='{ on }'>
                                                <v-icon  color="primary" v-on="on">mdi-truck-outline</v-icon>
                                            </template>
                                            <span>Allows planning tasks against {{ 'equipment' | termPlural }} instead of a {{ 'location' | term }}.</span>
                                        </v-tooltip>
                                        <v-tooltip v-else right>
                                            <template v-slot:activator='{ on }'>
                                                <v-icon  color="primary" v-on="on">mdi-map-marker-outline</v-icon>
                                            </template>
                                            <span>Standard {{ 'location' | term }}.</span>
                                        </v-tooltip>
                                    </td>
                                    <td>{{ item.name }}
                                        <v-btn v-if="item.children.length > 0" text small @click="focusOnStope(item)" class="ml-3 ordo-button--text-small">{{ item.children.length }} {{ item.children.length | pluralize('child', 'children') }}</v-btn>
                                        <v-tooltip right>
                                            <template v-slot:activator='{ on }'>
                                                <v-btn v-if="item.miningMethod === MiningMethodType.SubLevelStoping" icon color="secondary" small right @click="onClickAddChildLocation(item.id)" v-on="on"><v-icon >mdi-plus</v-icon></v-btn>
                                            </template>
                                            <span>Add a child {{ 'location' | term }}.</span>
                                        </v-tooltip>
                                    </td>
                                    <td>{{ item.reducedLevel.name }}</td>
                                    <td>{{ item.cycle ? item.cycle.name : '' }}</td>
                                    <td><span v-if="item.cycleType">{{ item.cycleType | cycleType }}</span></td>
                                    <td v-if="allowEquipmentLocations">{{ item.equipment ? item.equipment.name : '' }}</td>
                                    <td>
                                        <div class="no-wrap">
                                            {{ item.displayOrder }}
                                            <v-btn text icon @click="edit(item)">
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </standard-wrapping-reorderable>
                </v-window-item>
                <v-window-item>
                    <v-card-title v-if="!!selectedDepartment ">
                        <h2 class='subtitle-1'>
                            <v-tooltip right>
                                <template v-slot:activator='{ on }'>
                                    <v-btn @click="clearStopeFocus" small left icon v-on="on"><v-icon>mdi-arrow-left</v-icon></v-btn>
                                </template>
                                <span>Back</span>
                            </v-tooltip>
                            Child {{ 'Location' | termPlural }} of Stope {{ focussedStopeName }}
                            <v-tooltip right>
                                <template v-slot:activator='{ on }'>
                                    <v-btn icon right small color="secondary" @click="onClickAddChildLocation(focussedStopeId)" v-on="on"><v-icon>mdi-plus</v-icon></v-btn>
                                </template>
                                <span>Add a child {{ 'location' | term }}.</span>
                            </v-tooltip>
                        </h2>
                    </v-card-title>
                    <standard-wrapping-reorderable
                        v-if="!!selectedDepartment"
                        :items="selectedChildLocations"
                        :item-id="'id'"
                        :item-order="'displayOrder'"
                        @reordered="onChildItemsReordered"
                        item-class="locations-item"
                        handle-class="locations-handle"
                    >
                        <v-data-table
                            disable-pagination
                            hide-default-footer
                            :headers="childLocationHeaders"
                            :items="selectedChildLocations"

                        >
                            <template v-slot:item="{ item }">
                                <tr :class="{ dirty: item.dirty, 'locations-item': true }">
                                    <td>
                                        <div class="no-wrap">
                                            <v-icon class="locations-handle">mdi-drag</v-icon>
                                        </div>
                                    </td>
                                    <td>{{ item.name }}</td>
                                    <td>{{ item.reducedLevel.name }}</td>
                                    <td>
                                        <div class="no-wrap">
                                            {{ item.displayOrder }}
                                            <v-btn text icon @click="editChild(item)">
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </standard-wrapping-reorderable>
                </v-window-item>
            </v-window>

            <v-card-title v-if="!!selectedDepartment && filteredArchivedLocationsForDisplay.length > 0">
                <h2 class='subtitle-1'><v-icon color="primary" left>mdi-archive-outline</v-icon>Archived {{ 'Location' | termPlural }}</h2>
            </v-card-title>
            <v-data-table
                disable-pagination
                hide-default-footer
                :headers="locationHeaders"
                :items="filteredArchivedLocationsForDisplay"
                v-if="!!selectedDepartment && filteredArchivedLocationsForDisplay.length > 0"
            >
                <template v-slot:item="{ item }">
                    <tr :class="{ dirty: item.dirty }">
                        <td>

                        </td>
                        <td v-if="allowEquipmentLocations">
                            <v-tooltip v-if="item.equipmentId" right>
                                <template v-slot:activator='{ on }'>
                                    <v-icon  color="primary" v-on="on">mdi-truck-outline</v-icon>
                                </template>
                                <span>Allows planning tasks against {{ 'equipment' | termPlural }} instead of a {{ 'location' | term }}.</span>
                            </v-tooltip>
                            <v-tooltip v-else right>
                                <template v-slot:activator='{ on }'>
                                    <v-icon  color="primary" v-on="on">mdi-map-marker-outline</v-icon>
                                </template>
                                <span>Standard {{ 'location' | term }}.</span>
                            </v-tooltip>
                        </td>
                        <td>{{ item.name }}</td>
                        <td>{{ item.reducedLevel.name }}</td>
                        <td>{{ item.cycle ? item.cycle.name : '' }}</td>
                        <td><span v-if="item.cycleType">{{ item.cycleType | cycleType }}</span></td>
                        <td v-if="allowEquipmentLocations">{{ item.equipment ? item.equipment.name : '' }}</td>
                        <td>
                            <div class="no-wrap">
                                <v-btn text icon @click="edit(item)">
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </div>
                        </td>
                    </tr>
                </template>
            </v-data-table>
            <empty-state
                v-if="!selectedDepartment"
                icon="mdi-alert-circle-outline"
                :message="'Select a ' + $termSync('Department')"
            ></empty-state>
        </v-card>
        <location-creation-wizard
            :location-name-convention='locationNameConvention'
            :available-default-cycles='filteredDefaultCycles'
            :available-reduced-levels='filteredReducedLevels'
            :initial-information='newLocation'
            :available-parent-stopes="filteredOpenStopes"
            :archived-location-names='archivedLocationNames'
            v-if='newLocation'
            @save="saveNewLocation"
            @save-child-location="saveNewStopeChild"
            @cancel="cancelNewLocation"
        >
        </location-creation-wizard>
        <equipment-location-creation-wizard
            :available-reduced-levels='filteredReducedLevels'
            :initial-priority='newEquipmentLocation.priority'
            :archived-location-names='archivedLocationNames'
            :available-equipment='availableEquipment'
            v-if='newEquipmentLocation'
            @save="saveNewEquipmentLocation"
            @cancel="cancelNewLocation"
        >
        </equipment-location-creation-wizard>
        <child-location-editor
            v-if="!!selectedChildLocation"
            :location-name-convention="locationNameConvention"
            :other-children-display-orders="getOtherChildrenDisplayOrders(selectedChildLocation.locationId)"
            :reduced-levels="reducedLevels"
            :editing-location="selectedChildLocation"
            @cancel="cancelEditChild"
            @save="saveChildLocation"
        >

        </child-location-editor>
        <standard-dialog :render="!!selected" :value='showModal' :max-width="900" persistent>
            <template v-slot:title>
                <span v-if="selected.id">Edit</span>
                <span v-else>Create</span>
                <span class="ml-1">{{ 'Location' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref="form" :lazy-validation="true">
                    <v-container fluid>
                        <v-row>
                            <v-col cols="5" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    label="Name"
                                    :rules="[$rules.required(), $rules.maxLength(50), validateLocation]"
                                    :disabled="selected.isArchived"
                                    v-model="selected.name"
                                    counter="50"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="5" class="pa-2">
                                <v-select
                                    autocomplete="off"
                                    label="Level"
                                    :rules="[$rules.required()]"
                                    v-model="selected.reducedLevelId"
                                    :disabled="selected.isArchived"
                                    :items="filteredReducedLevels"
                                    item-value="id"
                                    item-text="name"
                                ></v-select>
                            </v-col>
                            <v-col cols="2" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    typoe="number"
                                    label="Priority"
                                    :disabled="selected.isArchived"
                                    :rules="[$rules.required(), $rules.min(1)]"
                                    v-model="selected.displayOrder"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6" class="pa-2">
                                <v-checkbox
                                    autocomplete="off"
                                    label="Ignores start-of-shift tasks?"
                                    :disabled="selected.isArchived"
                                    v-model="selected.ignoreStartOfShiftSpecial"
                                ></v-checkbox>
                            </v-col>
                            <v-col cols="6" class="pa-2">
                                <v-checkbox
                                    autocomplete="off"
                                    label="Independent end-of-shift?"
                                    :disabled="selected.isArchived"
                                    v-model="selected.ignoreEndOfShiftSpecial"
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <v-select
                                    autocomplete="off"
                                    label="Status"
                                    v-model="selected.status"
                                    :items="filteredStatuses"
                                    :disabled="selected.isArchived"
                                    item-value="id"
                                    :readonly="selected.id !== undefined || filteredStatuses.length == 1"
                                    item-text="name"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row v-if="showCycleType">
                            <v-col cols="6">
                                <v-select
                                    autocomplete="off"
                                    label="Cycle Type"
                                    v-model="selected.cycleType"
                                    :items="cycleTypes"
                                    item-value="id"
                                    :disabled="selected.isArchived"
                                    :readonly="selected.id !== undefined || cycleTypes.length == 1"
                                    item-text="name"
                                    @change="onChangeCycleType"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row v-if="showMiningMethod">
                            <v-col cols="6">
                                <v-select
                                    autocomplete="off"
                                    label="Mining Method"
                                    v-model="selected.miningMethod"
                                    :items="filteredMiningMethods"
                                    item-value="id"
                                    :disabled="selected.isArchived"
                                    :readonly="selected.id !== undefined || filteredMiningMethods.length == 1"
                                    :rules="[$rules.requiredAllowZero()]"
                                    item-text="name"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <v-select
                                    autocomplete="off"
                                    label="Default Cycle"
                                    v-model="selected.cycleId"
                                    :items="cyclesFor(selected.departmentId)"
                                    :disabled="selected.isArchived"
                                    item-value="id"
                                    item-text="name"
                                    v-if="isDuration"
                                ></v-select>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="6">
                                <v-text-field
                                    autocomplete="off"
                                    type="number"
                                    label="Start Cut Number"
                                    :disabled="selected.isArchived"
                                    :rules="[startCutNumberRule]"
                                    v-model.number="selected.startCutNumber"
                                    v-if="isDuration"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                    </v-container>
                </v-form>
                <v-alert
                    v-if='!selected.canDelete && !selected.loadingCanDelete && !selected.isArchived'
                    color='warning'
                    class="white--text">
                    This location has activities planned and cannot be deleted.
                </v-alert>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if='editingSelected && !selected.isArchived' @click='del()' :disabled='!selected.canDelete' :loading='selected.loadingCanDelete'>Delete</v-btn>
                <v-btn color="red" text v-if="editingSelected && !selected.isArchived" @click="archiveLocation"><v-icon left>mdi-archive-outline</v-icon>Archive</v-btn>
                <v-btn color="red" text v-if="editingSelected && selected.isArchived" @click="restoreLocation"><v-icon left>mdi-archive-arrow-up-outline</v-icon>Restore</v-btn>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>Cancel</v-btn>
                <v-btn color='primary' text v-if="!selected.isArchived" @click='save()'>Save</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>Cancel</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <confirm ref="confirm"></confirm>

    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import MineAreas from '@/lib/data/MineAreas';
import ReducedLevels from '@/lib/data/ReducedLevels';
import Locations from '@/lib/data/Locations';
import Cycles from '@/lib/data/Cycles';
import DownloadHelper from '@/lib/DownloadHelper';
import { CycleType } from '@/models/api/Enums/cycle-type';
import { MiningMethodType } from '@/models/api/Enums/mining-method-type';
import { enumOptions } from '@/lib/EnumExtensions';
import { LocationStatus } from '@/models/api/Data/location-status';
import {
    AlterLocationsInStopeCommand,
    CreateLocationCommand,
    DepartmentFeatureFlags,
    PlanningLocationType
} from '@/models/api';
import { IsEquipmentLocation, IsStandardLocation, ValidateLocationName } from '@/lib/services/Location';
import LocationCreationWizard, { LocationPurpose } from '@/components/Shared/Locations/LocationCreationWizard.vue';
import SplitButton from '@/components/Shared/Common/SplitButton.vue';
import Equipment from '@/lib/data/Equipment';
import EquipmentLocationCreationWizard from '@/components/Shared/Locations/EquipmentLocationCreationWizard.vue';
import { DepartmentHasFeature } from '@/lib/services/Department';
import StandardWrappingReorderable from '@/components/Shared/Common/StandardWrappingReorderable.vue';
import ChildLocationEditor from '@/components/Shared/Locations/ChildLocationEditor.vue';

export default {
    components: { ChildLocationEditor, StandardWrappingReorderable, SplitButton, LocationCreationWizard, EquipmentLocationCreationWizard },
    data() {
        return {
            data: null,
            mineAreas: null,
            departmentDropdownOptions: null,
            reducedLevels: null,
            cycles: null,
            equipment: [],
            cycleTypes: enumOptions(CycleType, this.$options.filters.cycleType),
            miningMethods: enumOptions(MiningMethodType, this.$options.filters.miningMethod),
            statuses: enumOptions(LocationStatus, this.$options.filters.locationStatus),
            selected: null,
            selectedChildLocation: null,
            editingSelected: false,
            newLocation: null,
            newEquipmentLocation: null,
            selectedLocationConfigs: null,
            selectedMineArea: null,
            selectedDepartment: null,
            showModal: false,
            importDialog: false,
            file: null,
            focussedStopeId: null,
            dataTableDisplayWindow: 0,
            baseLocationHeaders: [
                { text: '', value: 'order', sortable: false, width: '24px' },
                { text: '', value: 'locationType', sortable: false, width: '24px', onlyIfEquipmentAllowed: true },
                { text: 'Name', value: 'name', sortable: false, width: '40%' },
                { text: 'Level', value: 'reducedLevel.name', sortable: false, width: '25%' },
                { text: 'Cycle', value: 'cycle.name', sortable: false, width: '15%' },
                { text: 'Cycle Type', value: 'cycleType | cycleType', sortable: false, width: '20%' },
                { text: 'Equipment', value: 'equipment', sortable: false, width: '15%', onlyIfEquipmentAllowed: true },
                { text: '', value: 'actions', sortable: false, align: 'right' }
            ],
            childLocationHeaders: [
                { text: '', value: 'order', sortable: false, width: '24px' },
                { text: 'Name', value: 'name', sortable: false, width: '40%' },
                { text: 'Level', value: 'reducedLevel.name', sortable: false, width: '25%' },
                { text: '', value: 'actions', sortable: false, align: 'right' }
            ],
            equipmentLocationHeaders: [
                { text: 'Name', value: 'name', sortable: false, width: '40%' },
                { text: 'Level', value: 'reducedLevel.name', sortable: false, width: '25%' },
                { text: 'Equipment', value: 'cycle.name', sortable: false, width: '15%' },
                { text: '', value: 'actions', sortable: false, align: 'right' }
            ],
        };
    },
    async created() {
        await this.loadData();
    },
    destroyed() {},
    methods: {
        async loadData() {
            this.mineAreas = await MineAreas.get(['Departments']);
            this.reducedLevels = await ReducedLevels.get();
            this.cycles = await Cycles.get();
            this.equipment = await Equipment.get();
            var data = await Locations.get(['ReducedLevel', 'Cycle', 'Equipment']);
            for (let d of data) {
                d.dirty = false;
            }
            this.data = data;
            var departments = [];
            for (let mine of this.mineAreas) {
                departments.push({ id: mine.id, name: mine.name, isGroup: true, department: null });
                for (let dept of mine.departments) {
                    departments.push({
                        id: dept.id,
                        name: dept.name,
                        isGroup: false,
                        mineAreaId: mine.id,
                        department: dept
                    });
                }
            }
            this.departmentDropdownOptions = departments;
        },
        selectMineArea() {
            var selected = this.departmentDropdownOptions.filter(x => x.id == this.selectedDepartment)[0];
            this.selectedMineArea = selected.mineAreaId;
        },
        createDevelopmentLocation() {
            this.create(LocationPurpose.Development);
        },
        createAdHocLocation() {
            this.create(LocationPurpose.AdHoc);
        },
        createStope(){
            this.create(LocationPurpose.Stope);
        },
        createStopeChild(locationId){
            this.create(LocationPurpose.SubStope, locationId);
        },
        createSLC(){
            this.create(LocationPurpose.SLC);
        },
        createEquipmentLocation() {
            this.editingSelected = false;

            this.newEquipmentLocation = {
                priority: this.nextAvailableDisplayOrder,
            };
        },
        onClickAddLocation() {
            if(this.selectedDepartment == null)
                return;

            if(this.permittedPhysicalLocationTypesQuickAccess.length === 1)
                this.permittedPhysicalLocationTypesQuickAccess[0].action();
            else
                this.create();
        },
        onClickAddChildLocation(parentItemId){
            this.createStopeChild(parentItemId);
        },
        create(locationPurpose = null, parentLocationId = null){
            this.editingSelected = false;

            this.newLocation = {
                priority: parentLocationId != null ? this.getNextAvailableDisplayOrderForSubLocation(parentLocationId) : this.nextAvailableDisplayOrder,
                locationPurpose: locationPurpose,
                parentLocationId: parentLocationId
            }
        },
        edit(item) {
            this.selected = {
                ... item,
                loadingCanDelete: true,
                canDelete: false
            };
            this.editingSelected = true;

            Locations.hasPlannedWork(this.selected.id).then(response => {
                if(this.selected?.id === item.id)
                    this.selected.canDelete = response.hasWorkPlanned === false;
            }).finally(() => {
                if(this.selected?.id === item.id)
                    this.selected.loadingCanDelete = false;
            });

            this.showModal = true;
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        focusOnStope(item) {
            this.focussedStopeId = item.id;
            this.dataTableDisplayWindow=1;
        },
        clearStopeFocus() {
            this.focussedStopeId = null;
            this.dataTableDisplayWindow=0;
        },
        editChild(item){
            this.selectedChildLocation = {
                locationName: item.name,
                locationId: item.id,
                creating: false,
                displayOrder: item.displayOrder,
                isDrawpoint: item.isDrawpoint,
                reducedLevelId: item.reducedLevelId
            };
        },
        cancelEditChild(){
            this.selectedChildLocation = null;
        },
        async saveChildLocation(childLocation) {
            if(this.focussedStopeId == null)
                return;

            const alterationCommand = {
                _type: 'AlterLocationsInStopeCommand',
                stopeId: this.focussedStopeId,
                locationsToAlter: [],
                locationsToAdd: [],
                locationsToRemove: []
            };

            alterationCommand.locationsToAlter.push({
                _type: 'AlterLocationsInStopeLocationAlterationModel',
                locationId: childLocation.locationId,
                name: childLocation.locationName,
                isDrawpoint: childLocation.isDrawpoint ?? false,
                displayOrder: childLocation.displayOrder
            });

            await Locations.alterSubLocationsInStope(alterationCommand);

            this.selectedChildLocation = null;

            EventBus.$emit(Events.ToastSuccess, `Child ${this.$termSync('Location')} Saved`);
            await this.loadData();
        },
        async saveList() {
            for (let x of this.data) {
                if (!x.dirty) {
                    continue;
                }
                await Locations.update({
                    ...x,
                    _type: "UpdateLocationCommand"
                });
            }
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Location')} Saved`);
            await this.loadData();
        },
        cancelNewLocation() {
            this.newLocation = null;
            this.newEquipmentLocation = null;
        },
        async saveNewLocation(newLocation){
            await Locations.create({
                ... newLocation,
                departmentId: this.selectedDepartment,
                _type: "CreateLocationCommand",
            });
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Location')} Saved`);
            this.newLocation = null;
            await this.loadData();
        },
        async saveNewStopeChild(newChildLocation){
            await Locations.createStopeSubLocation({
                ...newChildLocation,
                _type: 'CreateStopeSubLocationCommand'
            });

            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Location')} Saved`);
            this.newLocation = null;
            await this.loadData();
        },
        async saveNewEquipmentLocation(newLocation){
            await Locations.createEquipmentLocation({
                ... newLocation,
                departmentId: this.selectedDepartment,
                _type: "CreateLocationCommand",
            });
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Location')} Saved`);
            this.newLocation = null;
            this.newEquipmentLocation = null;
            await this.loadData();
        },
        async save() {
            if (!this.$refs.form.validate()) {
                return;
            }
            if(this.editingEquipmentLocation)
                await this.saveEquipmentLocation(this.selected);
            else
                await this.saveStandardLocation(this.selected);

            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Location')} Saved`);
            this.showModal = false;
            await this.loadData();
        },
        async saveStandardLocation(location) {
            if(!this.selected.id)
                return;

            await Locations.update({
                ... this.selected,
                _type: "UpdateLocationCommand"
            });
        },
        async saveEquipmentLocation(equipmentLocation) {
            if(!this.selected.id)
                return;

            await Locations.updateEquipmentLocation({
                ... this.selected,
                _type: "UpdateEquipmentLocationCommand"
            });
        },
        async del() {
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Location'),  { })) {
                await Locations.delete(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Location')} Deleted`);
                this.showModal = false;
                await this.loadData();
            }
        },
        validateLocation(v) {
            var mine = this.mineAreas.filter(x => x.id == this.selectedMineArea)[0];
            var convention = mine.locationConvention;

            return ValidateLocationName(v, convention, this.selected?.cycleType ?? null);
        },
        startCutNumberRule(v) {
            if (v === null || v === undefined || v === '') return true;
            if (!Number.isInteger(v) || v < 1) return 'Must be a positive integer';
            return true;
        },
        cyclesFor(departmentId) {
            return this.cycles
                .filter(x => x.departmentId == departmentId)
                .filter(x => x.cycleType === this.selected.cycleType);
        },
        getNextAvailableDisplayOrderForSubLocation(parentLocationId) {
            return this.filteredActiveLocations.filter(x=>x.parentLocationId === parentLocationId).length ? this.filteredActiveLocations.filter(x=>x.parentLocationId === parentLocationId)[this.filteredActiveLocations.filter(x=>x.parentLocationId === parentLocationId).length - 1].displayOrder + 1 : 1;
        },
        getOtherChildrenDisplayOrders(childLocationId){
            if(this.focussedStopeId == null)
                return [];

            return this.selectedChildLocations.filter(x=>x.id !== childLocationId).map(x=>x.displayOrder);
        },
        async archiveLocation(){
            if(this.selected == null || this.selectedDepartment == null)
                return;

            this.$wait.start('saving');
            try {
                await Locations.archiveLocation(this.selected.id);
                await this.loadData();
            } finally {
                this.showModal = false;
                this.$wait.end('saving');
            }
        },
        async restoreLocation(){
            if(this.selected == null || this.selectedDepartment == null)
                return;

            this.$wait.start('saving');
            try {
                await Locations.restoreLocationFromArchive(this.selected.id);
                await this.loadData();
            } finally {
                this.showModal = false;
                this.$wait.end('saving');
            }
        },
        async exportData() {
            const result = await Locations.export(this.selectedDepartment);
            var csv = result.csvFile;
            var blob = DownloadHelper.makeBlobFromFileString(csv);
            var departmentName = this.selectedDepartmentModel.name.replace(/[/\\?%*:|"<>]/g, '');
            DownloadHelper.download(`Locations-${departmentName}.csv`, blob);
        },
        async importData() {
            try {
                this.$wait.start('saving');
                this.uploadResult = null;
                await Locations.import(this.selectedDepartment, this.file);
                await this.loadData();
            } finally {
                this.importDialog = false;
                this.file = null;
                this.$refs.inputUpload.value = null;
                this.$wait.end('saving');
            }
        },
        async getFile(e) {
            this.file = e.target.files[0];
        },
        onChangeCycleType() {
            if(this.selected.cycleType !== CycleType.Rate)
                this.selected.miningMethod = null;
        },
        onItemsReordered(reorderedItems) {
            // Update display orders based on new order
            reorderedItems.forEach((item, index) => {
                const originalItem = this.filteredActiveLocations.find(x => x.id === item.id);
                if (originalItem && originalItem.displayOrder !== index + 1) {
                    originalItem.displayOrder = index + 1;
                    originalItem.dirty = true;
                }
            });
        },
        async onChildItemsReordered(reorderedItems) {
            // Update display orders based on new order
            const alteredItems = reorderedItems.map((item, index) => {
                const originalItem = this.selectedChildLocations.find(x => x.id === item.id);
                if (originalItem && originalItem.displayOrder !== index + 1) {
                    originalItem.displayOrder = index + 1;
                }

                return originalItem;
            });

            // we're going to live update these
            await Locations.alterSubLocationsInStope({
                _type: 'AlterLocationsInStopeCommand',
                stopeId: this.focussedStopeId,
                locationsToAlter: alteredItems.map(x => ({
                    locationId: x.id,
                    isDrawpoint: x.isDrawpoint,
                    displayOrder: x.displayOrder,
                    name: x.name,
                })),
                locationsToRemove: [],
                locationsToAdd: []
            });
        }
    },
    computed: {
        MiningMethodType() {
            return MiningMethodType
        },
        locationHeaders() {
            return this.baseLocationHeaders.filter(x=>!x.onlyIfEquipmentAllowed || this.allowEquipmentLocations);
        },
        selectedDepartmentModel() {
            if (!this.data || !this.selectedDepartment) {
                return {};
            }
            return this.departmentDropdownOptions.filter(x => x.id == this.selectedDepartment)[0].department;
        },
        archivedLocationNames() {
            return this.filteredArchivedLocations.map(x=>x.name);
        },
        filteredActiveLocations() {
            return this.filtered.filter(x=>!x.isArchived);
        },
        filteredActiveLocationsForDisplay() {
            return this.filteredActiveLocations.filter(x=>x.parentLocationId == null).map(x=>({
                ... x,
                children: this.filteredActiveLocations.filter(y=>y.parentLocationId === x.id)
            }));
        },
        filteredActiveEquipmentLocations() {
            return this.filteredActiveLocations.filter(IsEquipmentLocation);
        },
        filteredArchivedLocations() {
            return this.filtered.filter(x=>x.isArchived);
        },
        filteredArchivedLocationsForDisplay() {
            return this.filteredArchivedLocations.filter(x=>x.parentLocationId == null).map(x=>({
                ... x,
                children: this.filteredArchivedLocations.filter(y=>y.parentLocationId === x.id)
            }));
        },
        filtered() {
            if (!this.data || !this.selectedDepartment) {
                return [];
            }
            const filtered = this.data
                .filter(x => x.departmentId == this.selectedDepartment)
                .sort((a, b) => a.displayOrder - b.displayOrder);
            return filtered;
        },
        selectedChildLocations() {
            if(this.focussedStopeId == null)
                return [];

            return this.filteredActiveLocations.filter(x => x.parentLocationId === this.focussedStopeId);
        },
        focussedStopeName() {
            if(this.focussedStopeId == null)
                return '';

            const stope = this.filteredActiveLocations.find(x => x.id === this.focussedStopeId);
            return stope ? stope.name : '';
        },
        filteredMiningMethods() {
            return this.miningMethods;
        },
        filteredStatuses() {
            return this.statuses;
        },
        filteredReducedLevels() {
            if (!this.data || !this.selectedDepartment) {
                return [];
            }
            const filtered = this.reducedLevels
                .filter(x => x.mineAreaId == this.selectedMineArea)
                .sort((a, b) => a.displayOrder - b.displayOrder);
            return filtered;
        },
        filteredDefaultCycles() {
            if(!this.selectedDepartment)
                return [];

            return this.cycles.filter(x=>x.departmentId == this.selectedDepartment && x.cycleType === CycleType.Duration);
        },
        filteredOpenStopes() {
            return this.filtered.filter(x=> x.miningMethod == MiningMethodType.SubLevelStoping && x.parentLocationId == null);
        },
        locationNameConvention() {
            var mine = this.mineAreas.filter(x => x.id == this.selectedMineArea)[0];
            return mine?.locationConvention;
        },
        nextAvailableDisplayOrder() {
            return this.filteredActiveLocations.length ? this.filteredActiveLocations[this.filteredActiveLocations.length - 1].displayOrder + 1 : 1;
        },
        availableEquipment() {
            if(this.selectedDepartment == null)
                return [];

            return this.equipment
                .filter(x=>!x.isArchived)
                .filter(x=>!this.filteredActiveEquipmentLocations.some(y=>y.equipmentId === x.id))
                .map(x=>({ id: x.id, name: x.name }));
        },
        editingEquipmentLocation() {
            return this.selected?.equipmentId != null;
        },
        dirty() {
            for (let x of this.data) {
                if (x.dirty) {
                    return true;
                }
            }
            return false;
        },
        showCycleType() {
            return !this.editingEquipmentLocation;
        },
        showMiningMethod() {
            return !this.editingEquipmentLocation && this.isRateBased;
        },
        allowDevelopmentLocations(){
            return this.selectedDepartmentModel && !DepartmentHasFeature(this.selectedDepartmentModel.featureFlags, DepartmentFeatureFlags.Maintenance);
        },
        allowProductionLocations() {
            return this.selectedDepartmentModel && !DepartmentHasFeature(this.selectedDepartmentModel.featureFlags, DepartmentFeatureFlags.Maintenance);
        },
        allowAdHocLocations(){
            return this.selectedDepartmentModel;
        },
        allowEquipmentLocations() {
            return this.selectedDepartmentModel && DepartmentHasFeature(this.selectedDepartmentModel.featureFlags, DepartmentFeatureFlags.Maintenance);
        },
        isDuration() {
            return this.selected.cycleType === CycleType.Duration;
        },
        isRateBased() {
            return this.selected.cycleType === CycleType.Rate;
        },
        permittedPhysicalLocationTypesQuickAccess() {
            return this.permittedLocationTypesQuickAccess.filter(x=>x.planningLocationType === PlanningLocationType.Location);
        },
        permittedLocationTypesQuickAccess() {
            return [
                {
                    name: 'Development',
                    icon: 'mdi-timelapse',
                    enabled: this.allowDevelopmentLocations,
                    action: this.createDevelopmentLocation,
                    planningLocationType: PlanningLocationType.Location
                },
                {
                    name: 'Ad-Hoc',
                    icon: 'mdi-timelapse',
                    enabled: this.allowAdHocLocations,
                    action: this.createAdHocLocation,
                    planningLocationType: PlanningLocationType.Location
                },
                {
                    name: 'Stope',
                    icon: 'mdi-timelapse',
                    enabled: this.allowProductionLocations,
                    action: this.createStope,
                    planningLocationType: PlanningLocationType.Location
                },
                {
                    name: 'SLC',
                    icon: 'mdi-timelapse',
                    enabled: this.allowProductionLocations,
                    action: this.createSLC,
                    planningLocationType: PlanningLocationType.Location
                },
                {
                    name: this.$termSync('Equipment'),
                    icon: 'mdi-timelapse',
                    enabled: this.allowEquipmentLocations,
                    action: this.createEquipmentLocation,
                    planningLocationType: PlanningLocationType.Equipment
                }
            ].filter(x=>x.enabled);
        }
    }
};
</script>

<style lang="scss"></style>
